import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmModal from '../components/common/ConfirmModal';
import { XIcon, PlusIcon } from '../components/common/Icons';
import useIsMobile from '../hooks/useIsMobile';
import usePermissions from '../hooks/usePermissions';
import { Req } from '../utils/formErrors';

const STATUS_LABELS = { nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité' };
const REQ_STATUS_COLORS = {
  pending:  { bg: '#fffbeb', color: '#92400e', border: '#fef08a', label: 'En attente' },
  approved: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', label: 'Approuvé' },
  rejected: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Rejeté' },
};

const TARGET_STATUS_OPTS = [
  { value: 'nouveau',   label: 'Nouveau' },
  { value: 'membre',    label: 'Membre régulier' },
  { value: 'serviteur', label: 'Serviteur' },
];

const EMPTY_RULE = { from_status: 'invite', to_status: 'nouveau', min_months: 3, min_attendances: 10, is_active: true };

const styles = {
  page:    { width: '100%' },
  title:   { fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' },
  tabs:    { display: 'flex', gap: '4px', marginBottom: '22px', backgroundColor: 'white', borderRadius: '10px', padding: '4px', border: '1px solid #e2e8f0', width: 'fit-content' },
  tabBtn:  (active) => ({
    padding: '9px 20px', borderRadius: '7px', border: 'none', cursor: 'pointer',
    fontSize: '0.88rem', fontWeight: 600,
    backgroundColor: active ? '#1e40af' : 'transparent',
    color: active ? 'white' : '#64748b',
    transition: 'all 0.15s',
  }),
  card:    { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  th:      { padding: '11px 16px', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td:      { padding: '13px 16px', fontSize: '0.9rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  badge:   { display: 'inline-block', fontSize: '0.73rem', padding: '3px 9px', borderRadius: '999px', fontWeight: 600, border: '1px solid transparent' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal:   { backgroundColor: 'white', borderRadius: '14px', padding: '26px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  formInput: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '0.93rem', color: '#1e293b', boxSizing: 'border-box' },
  formLabel: { display: 'block', fontSize: '0.84rem', fontWeight: 600, color: '#374151', marginBottom: '5px' },
  formGroup: { marginBottom: '13px' },
  btnPrimary: { backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 },
  btnSecondary: { backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', padding: '8px 16px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 },
  btnSuccess: { backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '7px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 },
  btnDanger:  { backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 },
};

export default function MembershipAdmin() {
  const { user } = useAuth();
  const mob = useIsMobile();
  const [tab, setTab] = useState('requests');

  // ── Requests tab ──
  const [requests, setRequests]   = useState([]);
  const [reqFilter, setReqFilter] = useState('pending');
  const [loadReq, setLoadReq]     = useState(true);
  const [actionModal, setActionModal] = useState(null); // {req, mode:'approve'|'reject'}
  const [targetStatus, setTargetStatus] = useState('nouveau');
  const [rejectNotes, setRejectNotes]   = useState('');
  const [saving, setSaving]       = useState(false);

  // ── Rules tab ──
  const [rules, setRules]         = useState([]);
  const [loadRules, setLoadRules] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleForm, setRuleForm]   = useState(EMPTY_RULE);
  const [editRuleId, setEditRuleId] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [running, setRunning]     = useState(false);
  const [confirmDeleteRule, setConfirmDeleteRule] = useState(null);

  const FROM_STATUS_OPTS = [
    { value: 'invite',   label: 'Invité' },
    { value: 'nouveau',  label: 'Nouveau' },
    { value: 'membre',   label: 'Membre régulier' },
  ];

  const fetchRequests = useCallback(async () => {
    setLoadReq(true);
    try {
      const res = await api.get(`/participants/admin/membership-requests/?status=${reqFilter}`);
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Erreur chargement demandes.'); }
    finally { setLoadReq(false); }
  }, [reqFilter]);

  const fetchRules = useCallback(async () => {
    setLoadRules(true);
    try {
      const res = await api.get('/participants/admin/upgrade-rules/');
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Erreur chargement règles.'); }
    finally { setLoadRules(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { if (tab === 'rules') fetchRules(); }, [tab, fetchRules]);

  const handleAction = async () => {
    if (!actionModal) return;
    setSaving(true);
    try {
      const payload = actionModal.mode === 'approve'
        ? { action: 'approve', target_status: targetStatus }
        : { action: 'reject',  notes: rejectNotes };
      await api.post(`/participants/admin/membership-requests/${actionModal.req.id}/action/`, payload);
      toast.success(actionModal.mode === 'approve' ? 'Demande approuvée.' : 'Demande rejetée.');
      setActionModal(null);
      fetchRequests();
    } catch { toast.error('Erreur lors de l\'action.'); }
    finally { setSaving(false); }
  };

  const openApprove = (req) => { setTargetStatus('nouveau'); setActionModal({ req, mode: 'approve' }); };
  const openReject  = (req) => { setRejectNotes('');         setActionModal({ req, mode: 'reject' });  };

  const handleSaveRule = async () => {
    setSaving(true);
    try {
      if (editRuleId) {
        await api.patch(`/participants/admin/upgrade-rules/${editRuleId}/`, ruleForm);
        toast.success('Règle mise à jour.');
      } else {
        await api.post('/participants/admin/upgrade-rules/', ruleForm);
        toast.success('Règle créée.');
      }
      setShowRuleModal(false);
      setEditRuleId(null);
      setRuleForm(EMPTY_RULE);
      fetchRules();
    } catch { toast.error('Erreur lors de la sauvegarde.'); }
    finally { setSaving(false); }
  };

  const handleDeleteRule = async (id) => {
    try {
      await api.delete(`/participants/admin/upgrade-rules/${id}/`);
      toast.success('Règle supprimée.');
      setConfirmDeleteRule(null);
      fetchRules();
    } catch { toast.error('Erreur lors de la suppression.'); }
  };

  const handleRunAutoUpgrade = async () => {
    setRunning(true); setRunResult(null);
    try {
      const res = await api.post('/participants/admin/run-auto-upgrade/');
      setRunResult(res.data);
      toast.success(`${res.data.upgraded_count} participant(s) promu(s).`);
      fetchRules();
    } catch { toast.error('Erreur lors de la promotion automatique.'); }
    finally { setRunning(false); }
  };

  const isPlatformAdmin = user?.role === 'superadmin_platform';
  const { hasGroupAccess } = usePermissions();
  const canAdmin = user?.role === 'admin' || user?.role === 'superviseur' || user?.role === 'responsable' || isPlatformAdmin || hasGroupAccess('participants');
  if (!canAdmin) return <div style={{ color: '#64748b', padding: '40px', textAlign: 'center' }}>Accès réservé aux administrateurs.</div>;

  return (
    <div style={styles.page}>
      <ConfirmModal
        open={!!confirmDeleteRule}
        title="Supprimer cette règle ?"
        message="Cette règle de promotion automatique sera définitivement supprimée."
        onConfirm={() => handleDeleteRule(confirmDeleteRule)}
        onCancel={() => setConfirmDeleteRule(null)}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={styles.title}>Demandes d'adhésion</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
            Gérez les demandes de devenir membre et les règles de promotion automatique.
          </p>
        </div>
      </div>

      {/* Onglets */}
      <div style={styles.tabs}>
        <button style={styles.tabBtn(tab === 'requests')} onClick={() => setTab('requests')}>Demandes</button>
        <button style={styles.tabBtn(tab === 'rules')} onClick={() => setTab('rules')}>Règles de promotion</button>
      </div>

      {/* ── TAB: REQUESTS ── */}
      {tab === 'requests' && (
        <>
          {/* Filtre statut */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {['pending','approved','rejected'].map(s => {
              const sc = REQ_STATUS_COLORS[s];
              return (
                <button key={s} onClick={() => setReqFilter(s)}
                  style={{ padding: '6px 16px', borderRadius: '999px', border: `1.5px solid ${reqFilter === s ? sc.color : '#d1d5db'}`, backgroundColor: reqFilter === s ? sc.bg : 'white', color: reqFilter === s ? sc.color : '#64748b', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 }}>
                  {sc.label}
                </button>
              );
            })}
          </div>

          {loadReq ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}><LoadingSpinner size={32} /></div>
          ) : requests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              Aucune demande {REQ_STATUS_COLORS[reqFilter].label.toLowerCase()}.
            </div>
          ) : (
            <div style={{ ...styles.card, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Participant</th>
                    {isPlatformAdmin && <th style={styles.th}>Église</th>}
                    <th style={styles.th}>Statut actuel</th>
                    <th style={styles.th}>Infos complémentaires</th>
                    <th style={styles.th}>Motivation</th>
                    <th style={styles.th}>Date demande</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(req => {
                    const sc = REQ_STATUS_COLORS[req.status];
                    return (
                      <tr key={req.id}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700 }}>{req.participant_name}</div>
                          <div style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' }}>{req.participant_member_code}</div>
                          {req.phone_req && <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: '2px' }}>{req.phone_req}</div>}
                        </td>
                        {isPlatformAdmin && (
                          <td style={styles.td}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e40af' }}>
                              {req.church_name || <span style={{ color: '#94a3b8' }}>—</span>}
                            </span>
                          </td>
                        )}
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, backgroundColor: '#f1f5f9', color: '#475569', borderColor: '#e2e8f0' }}>
                            {STATUS_LABELS[req.participant_status] || req.participant_status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {req.birth_date_req && <span>Né(e) le : {req.birth_date_req}</span>}
                            {req.city_req && <span>Ville : {req.city_req}</span>}
                            {req.profession_req && <span>Profession : {req.profession_req}</span>}
                            {req.previous_church && <span>Église préc. : {req.previous_church}</span>}
                            {req.baptised && <span style={{ color: '#1e40af', fontWeight: 600 }}>Baptisé(e){req.baptism_date ? ` (${req.baptism_date})` : ''}</span>}
                          </div>
                        </td>
                        <td style={{ ...styles.td, maxWidth: '200px' }}>
                          <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                            {req.motivation || '—'}
                          </div>
                        </td>
                        <td style={{ ...styles.td, fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {new Date(req.requested_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td style={styles.td}>
                          {req.status === 'pending' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <button style={styles.btnSuccess} onClick={() => openApprove(req)}>Approuver</button>
                              <button style={styles.btnDanger}  onClick={() => openReject(req)}>Rejeter</button>
                            </div>
                          ) : (
                            <div>
                              <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.color, borderColor: sc.border }}>
                                {sc.label}
                              </span>
                              {req.reviewed_by && <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: '4px' }}>par {req.reviewed_by}</div>}
                              {req.admin_notes && <div style={{ fontSize: '0.73rem', color: '#64748b', marginTop: '3px', fontStyle: 'italic' }}>{req.admin_notes}</div>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── TAB: RULES ── */}
      {tab === 'rules' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '3px' }}>Règles de promotion automatique</div>
              <div style={{ fontSize: '0.83rem', color: '#64748b' }}>
                Définissez quand un participant change automatiquement de statut en fonction de son ancienneté et de ses présences.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                style={{ ...styles.btnPrimary, backgroundColor: '#16a34a', display: 'flex', alignItems: 'center', gap: '6px' }}
                onClick={handleRunAutoUpgrade} disabled={running}>
                {running ? 'Calcul...' : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                    Lancer la promotion auto
                  </>
                )}
              </button>
              {/* Règles communes à toutes les églises : modifiables par le superadmin uniquement */}
              {isPlatformAdmin ? (
                <button
                  style={{ ...styles.btnPrimary, display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => { setRuleForm(EMPTY_RULE); setEditRuleId(null); setShowRuleModal(true); }}>
                  <PlusIcon size={15} color="white"/>
                  Nouvelle règle
                </button>
              ) : (
                <span style={{ fontSize: '0.78rem', color: '#64748b', alignSelf: 'center' }}>
                  Règles communes à toutes les églises — modifiables par le super administrateur
                </span>
              )}
            </div>
          </div>

          {/* Résultat auto-upgrade */}
          {runResult && (
            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, color: '#15803d', marginBottom: '8px' }}>
                Résultat : {runResult.upgraded_count} participant(s) promu(s)
              </div>
              {runResult.upgraded.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {runResult.upgraded.map((u, i) => (
                    <div key={i} style={{ fontSize: '0.84rem', color: '#166534', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{u.participant}</span>
                      <span style={{ color: '#64748b' }}>({u.member_code})</span>
                      <span>{STATUS_LABELS[u.from]} → <strong>{STATUS_LABELS[u.to]}</strong></span>
                      <span style={{ color: '#64748b' }}>{u.attendances} présences</span>
                    </div>
                  ))}
                </div>
              )}
              <button style={{ marginTop: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#64748b' }}
                onClick={() => setRunResult(null)}>Fermer</button>
            </div>
          )}

          {loadRules ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}><LoadingSpinner size={32} /></div>
          ) : rules.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              Aucune règle définie. Créez votre première règle de promotion.
            </div>
          ) : (
            <div style={{ ...styles.card, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Statut source</th>
                    <th style={styles.th}>Statut cible</th>
                    <th style={styles.th}>Ancienneté min.</th>
                    <th style={styles.th}>Présences min.</th>
                    <th style={styles.th}>Active</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule, idx) => (
                    <tr key={rule.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: '#f1f5f9', color: '#475569', borderColor: '#e2e8f0' }}>
                          {STATUS_LABELS[rule.from_status] || rule.from_status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>
                          {STATUS_LABELS[rule.to_status] || rule.to_status}
                        </span>
                      </td>
                      <td style={styles.td}>{rule.min_months} mois</td>
                      <td style={styles.td}>{rule.min_attendances} présences</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: rule.is_active ? '#f0fdf4' : '#f8fafc', color: rule.is_active ? '#166534' : '#94a3b8', borderColor: rule.is_active ? '#bbf7d0' : '#e2e8f0' }}>
                          {rule.is_active ? 'Active' : 'Désactivée'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {isPlatformAdmin && <div style={{ display: 'flex', gap: '6px' }}>
                          <button style={styles.btnSecondary}
                            onClick={() => { setRuleForm({ from_status: rule.from_status, to_status: rule.to_status, min_months: rule.min_months, min_attendances: rule.min_attendances, is_active: rule.is_active }); setEditRuleId(rule.id); setShowRuleModal(true); }}>
                            Modifier
                          </button>
                          <button style={styles.btnDanger} onClick={() => setConfirmDeleteRule(rule.id)}>Suppr.</button>
                        </div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal action (approve/reject) */}
      {actionModal && (
        <div style={{ ...styles.overlay, alignItems: mob ? 'flex-end' : 'center', padding: mob ? 0 : '16px' }} onClick={() => setActionModal(null)}>
          <div style={{ ...styles.modal, borderRadius: mob ? '20px 20px 0 0' : '14px', maxWidth: mob ? '100%' : '500px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                {actionModal.mode === 'approve' ? 'Approuver la demande' : 'Rejeter la demande'}
              </div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }} onClick={() => setActionModal(null)}>
                <XIcon size={18} color="#64748b"/>
              </button>
            </div>

            <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, color: '#1e293b' }}>{actionModal.req.participant_name}</div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '3px', fontFamily: 'monospace' }}>{actionModal.req.participant_member_code}</div>
              {actionModal.req.motivation && (
                <div style={{ fontSize: '0.83rem', color: '#475569', marginTop: '8px', fontStyle: 'italic', lineHeight: 1.5 }}>
                  « {actionModal.req.motivation} »
                </div>
              )}
            </div>

            {actionModal.mode === 'approve' && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Statut attribué<Req /></label>
                <select style={styles.formInput} value={targetStatus} onChange={e => setTargetStatus(e.target.value)}>
                  {TARGET_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                  Le participant recevra ce statut et sera considéré comme membre de l'église.
                </p>
              </div>
            )}

            {actionModal.mode === 'reject' && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Note explicative (facultatif)</label>
                <textarea
                  style={{ ...styles.formInput, minHeight: '80px', resize: 'vertical' }}
                  value={rejectNotes} onChange={e => setRejectNotes(e.target.value)}
                  placeholder="Raison du refus, démarches à effectuer..."
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button style={styles.btnSecondary} onClick={() => setActionModal(null)}>Annuler</button>
              <button
                style={{ ...styles.btnPrimary, backgroundColor: actionModal.mode === 'approve' ? '#16a34a' : '#b91c1c' }}
                onClick={handleAction} disabled={saving}>
                {saving ? 'En cours...' : actionModal.mode === 'approve' ? 'Confirmer l\'approbation' : 'Confirmer le rejet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal règle */}
      {showRuleModal && (
        <div style={{ ...styles.overlay, alignItems: mob ? 'flex-end' : 'center', padding: mob ? 0 : '16px' }} onClick={() => setShowRuleModal(false)}>
          <div style={{ ...styles.modal, borderRadius: mob ? '20px 20px 0 0' : '14px', maxWidth: mob ? '100%' : '500px', width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                {editRuleId ? 'Modifier la règle' : 'Nouvelle règle de promotion'}
              </div>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }} onClick={() => setShowRuleModal(false)}>
                <XIcon size={18} color="#64748b"/>
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Statut source</label>
                <select style={styles.formInput} value={ruleForm.from_status} onChange={e => setRuleForm({ ...ruleForm, from_status: e.target.value })}>
                  {FROM_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Statut cible</label>
                <select style={styles.formInput} value={ruleForm.to_status} onChange={e => setRuleForm({ ...ruleForm, to_status: e.target.value })}>
                  {TARGET_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Ancienneté min. (mois)</label>
                <input type="number" min="1" style={styles.formInput} value={ruleForm.min_months}
                  onChange={e => setRuleForm({ ...ruleForm, min_months: parseInt(e.target.value) || 1 })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Présences min. sur la période</label>
                <input type="number" min="1" style={styles.formInput} value={ruleForm.min_attendances}
                  onChange={e => setRuleForm({ ...ruleForm, min_attendances: parseInt(e.target.value) || 1 })} />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
              <input type="checkbox" id="ruleActive" checked={ruleForm.is_active}
                onChange={e => setRuleForm({ ...ruleForm, is_active: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer' }}/>
              <label htmlFor="ruleActive" style={{ fontSize: '0.9rem', color: '#374151', cursor: 'pointer' }}>Règle active</label>
            </div>

            <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 13px', fontSize: '0.8rem', color: '#0369a1', marginBottom: '16px' }}>
              <strong>Exemple :</strong> Si statut = <em>{STATUS_LABELS[ruleForm.from_status]}</em>, ancienneté ≥ {ruleForm.min_months} mois et présences ≥ {ruleForm.min_attendances} sur la période → passer à <em>{STATUS_LABELS[ruleForm.to_status]}</em>
            </div>

            <div style={{ display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button style={styles.btnSecondary} onClick={() => setShowRuleModal(false)}>Annuler</button>
              <button style={styles.btnPrimary} onClick={handleSaveRule} disabled={saving}>
                {saving ? 'Sauvegarde...' : editRuleId ? 'Mettre à jour' : 'Créer la règle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
